import { jsx as _jsx } from "react/jsx-runtime";
import { usePagedListing, useSafeSistaAsync, } from "@sista/library-stasi";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderTags, SelectTagger } from "@sista/service-tagger-ui";
import { DataGridPro, GridActionsCellItem, GridToolbar, gridClasses, csCZ, } from "@mui/x-data-grid-pro";
import { Edit } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { DistaAutocomplete } from "@sista/dista-ui";
import { useSpravaTextuClient } from "../../util/useSpravaTextuClient";
import { useStateName } from "../../util/useStateName";
import { TextStateChip } from "./TextStateChip";
const pageSize = 100;
const taggerNamespace = "spratex";
const iconSize = { width: "20px", height: "20px" };
export const findDefaultTexts = (data) => {
    if (data == null) {
        return null;
    }
    for (const text of data.texts) {
        if (text.providerUrn == null &&
            text.programUrn == null &&
            text.cfpUrn == null) {
            return text;
        }
    }
    return null;
};
export const TextsTable = ({ canEdit, selectedNamespaces, isSmall, editedText, onSelectedChange, onEditText, isChanged, onRefresh, isEditDone, isRowSelectable, isClosed, }) => {
    const { t } = useTranslation("sprava-textu");
    const spratex = useSpravaTextuClient();
    const keyId = useParams()?.keyId ?? null;
    const getStateName = useStateName();
    const navigate = useNavigate();
    const [selectedRowsData, setSelectedRowsData] = useState([]);
    const [filterValue, setFilterValue] = useState(null);
    const [quickSearchValue, setQuickSearchValue] = useState(null);
    const [getAllTextsCall, getAllTextsState] = useSafeSistaAsync(spratex.findTexts);
    const colorClasses = {
        [`.${gridClasses.row}.selected`]: {
            backgroundColor: "#b9d5ff91",
            color: "#1a3e72",
        },
        [`.${gridClasses.row}.none`]: {
            backgroundColor: "white",
            color: "black",
        },
        "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
            display: isRowSelectable ? "flex" : " none",
        },
    };
    const paramsForFilter = useMemo(() => {
        if (filterValue == null || filterValue.length === 0) {
            return {};
        }
        //console.log("FILTER VALUE", filterValue);
        // Convert filterValue to the expected format
        const params = filterValue?.map((item) => {
            if (item.field === "namespace") {
                return {
                    namespaceQuery: item.value ?? null,
                };
            }
            if (item.field === "key") {
                return {
                    keyQuery: item.value ?? null,
                };
            }
            if (item.field === "state") {
                return {
                    states: item.value ?? null,
                };
            }
            if (item.field === "taggerIds") {
                return {
                    taggerIds: item.value ?? null,
                };
            }
            if (item.field === "cs") {
                return {
                    translationsQuery: item.value ?? null,
                };
            }
            if (item.field === "en") {
                return {
                    translationsQuery: item.value ?? null,
                };
            }
            return {};
        });
        //console.log("PARAMS FOR FILTER", JSON.stringify(params));
        if (params.length === 1) {
            return params[0];
        }
        if (params.length > 1) {
            const joinedParams = Object.assign({}, ...params);
            //console.log("JOINED PARAMS", JSON.stringify(joinedParams));
            return joinedParams;
        }
    }, [filterValue]);
    const loader = useCallback(async (page) => {
        const result = await getAllTextsCall({
            page: page,
            pageSize: pageSize,
            //namespaces: selectedNamespaces ? [selectedNamespaces] : null,
            ...paramsForFilter,
            search: quickSearchValue,
        });
        if (result == null) {
            return [];
        }
        return result;
    }, [
        getAllTextsCall,
        paramsForFilter,
        quickSearchValue,
    ]);
    const { currentPage, error, pending, refresh, rows } = usePagedListing({
        loader,
        maxRows: 1000,
    });
    const [rowsValues, setRowsValues] = useState([]);
    useEffect(() => {
        setRowsValues(rows);
    }, [rows]);
    useEffect(() => {
        if (editedText == null)
            return;
        const changeRowForEditedText = rowsValues.find((row) => row.namespace === editedText.namespace && row.key === editedText.key);
        if (changeRowForEditedText == null)
            return;
        const indexOfEditedText = rowsValues.indexOf(changeRowForEditedText);
        if (indexOfEditedText === -1)
            return;
        const newRows = [...rowsValues];
        newRows[indexOfEditedText] = editedText;
        setRowsValues(newRows);
    }, [editedText, rowsValues]);
    useEffect(() => {
        if (selectedNamespaces) {
            //console.log("nastavujeme filtr pro namespace: ", selectedNamespaces);
            setFilterValue((prev) => [
                ...(prev || []),
                {
                    field: "namespace",
                    operator: "contains",
                    value: selectedNamespaces,
                },
            ]);
            onRefresh?.();
        }
    }, [selectedNamespaces, onRefresh]);
    const findTextByKey = useCallback((key) => {
        if (rows == null || rows.length === 0) {
            return null;
        }
        if (key == null || key === "") {
            return null;
        }
        return rows.find((row) => row.namespace + "|" + row.key === key);
    }, [rows]);
    const [editText, setEditText] = useState(keyId ? findTextByKey(keyId) : null);
    const [nextText, setNextText] = useState(keyId ? rows[rows.indexOf(findTextByKey(keyId)) + 1] : null);
    const onFilterChange = useCallback((filterModel) => {
        // Here you save the data you need from the filter model
        //console.log("FILTER MODEL CHANGED", filterModel);
        if (filterModel.quickFilterValues != null) {
            //console.log("QUICK FILTER VALUES", filterModel.quickFilterValues);
            setQuickSearchValue(filterModel.quickFilterValues.join(" "));
            refresh();
        }
        else {
            setQuickSearchValue(null);
            refresh();
        }
        if (filterModel == null ||
            filterModel.items == null ||
            filterModel.items.length == 0) {
            if (filterValue == null) {
                // no change
                return;
            }
            setFilterValue(null);
            refresh();
            return;
        }
        //console.log("FILTER MODEL", filterModel);
        let f = filterModel.items.map((item) => {
            return {
                field: item.field,
                operator: item.operator,
                value: item.value,
            };
        });
        if (f.length == 0)
            f = null;
        if (f == filterValue) {
            // no change
            return;
        }
        setFilterValue(f);
        refresh();
    }, [filterValue, refresh]);
    useEffect(() => {
        if (isChanged && keyId == null) {
            refresh();
            if (onRefresh) {
                onRefresh();
            }
        }
    }, [isChanged, refresh, onRefresh, keyId]);
    useEffect(() => {
        if (isEditDone) {
            if (onEditText) {
                onEditText(nextText);
            }
            setEditText(nextText);
            setNextText(rows[rows.indexOf(nextText) + 1] ?? null);
            if (nextText == null) {
                navigate("/");
                return;
            }
            else {
                navigate(`/keys/${nextText.namespace}|${nextText.key}`);
            }
            if (onRefresh) {
                onRefresh();
            }
        }
    }, [isEditDone, editText, onEditText, nextText, rows, onRefresh, navigate]);
    useEffect(() => {
        if (isClosed) {
            setEditText(null);
            setNextText(null);
            if (onRefresh) {
                onRefresh();
            }
        }
    }, [isClosed, onRefresh]);
    useEffect(() => {
        if (keyId != null && findTextByKey(keyId) != null) {
            setSelectedRowsData(null);
            if (onEditText) {
                onEditText(findTextByKey(keyId));
            }
        }
    }, [keyId, findTextByKey, onEditText]);
    /* namespace: string;
      key: string;
      taggerIds: string[];
      state: "APPROVED" | "NEW" | "UNDER_REVIEW" | "DEPRECATED";
      lastChange: string;
      texts: {
          cs: string;
          en?: string;
      };
      plannedUpdate?: string;
      dateDeleted?: string;
      providerUrn?: string;
      programUrn?: string;
      callForProposalUrn?: string; */
    const isSmallTable = useMemo(() => {
        return isSmall ?? false;
    }, [isSmall]);
    const tagFilterOperator = useMemo(() => [
        {
            label: t("sprava-textu.textsTable.containsTag", "obsahuje"),
            value: "containsTag",
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) {
                    return null;
                }
                return () => {
                    return true;
                };
            },
            InputComponent: (props) => (_jsx(SelectTagger, { namespace: "spratex", multiple: true, selectedIdsValues: filterValue?.[0]?.value ?? undefined, onChange: (data) => {
                    setFilterValue([
                        {
                            field: "taggerIds",
                            operator: "containsTag",
                            value: data,
                        },
                    ]);
                    refresh();
                }, sxProps: { justifySelf: "center" } })),
        },
    ], [t, filterValue, setFilterValue, refresh]);
    const stateOptions = useMemo(() => {
        return ["MISSING_TEXT", "MISSING_TRANSLATION", "NEED_REVIEW", "TRANSLATED"];
    }, []);
    const [selectedStateOptions, setSelectedStateOptions] = useState([]);
    const stateFilterOperator = useMemo(() => [
        {
            label: t("sprava-textu.textsTable.containsState", "obsahuje"),
            value: "containsState",
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) {
                    return null;
                }
                return () => {
                    return true;
                };
            },
            InputComponent: (props) => (_jsx(DistaAutocomplete, { multiple: true, options: stateOptions, getOptionLabel: (option) => getStateName(option) ??
                    t("sprava-textu.textsTable.unknownState", "Neznámý stav"), value: selectedStateOptions ?? undefined, size: "small", isOptionEqualToValue: (option, selectedValue) => {
                    return option === selectedValue;
                }, onChange: (e, newData) => {
                    setSelectedStateOptions(newData);
                    if (newData.length === 0) {
                        setFilterValue(null);
                        return refresh();
                    }
                    setFilterValue([
                        {
                            field: "state",
                            operator: "containsState",
                            value: newData,
                        },
                    ]);
                    refresh();
                }, sx: { minWidth: "10em" } })),
        },
    ], [
        t,
        setFilterValue,
        refresh,
        getStateName,
        stateOptions,
        selectedStateOptions,
        setSelectedStateOptions,
    ]);
    const columns = useMemo(() => {
        return [
            {
                field: "key",
                headerName: t("sprava-textu.textsTable.key", "Klíč"),
                sortable: true,
                flex: 1,
            },
            {
                field: "state",
                headerName: t("sprava-textu.textsTable.state", "Stav"),
                sortable: true,
                width: 150,
                filterOperators: stateFilterOperator,
                valueGetter: (params) => {
                    if (params.row.state == null)
                        return "";
                    return params.row.state;
                },
                renderCell: (params) => {
                    return params.row.state != null ? (_jsx(TextStateChip, { state: params.row.state, variant: "filled", size: "small" })) : ("");
                },
            },
            {
                field: "taggerIds",
                headerName: t("sprava-textu.textsTable.taggerIds", "Štítky"),
                sortable: true,
                flex: 1,
                filterOperators: tagFilterOperator,
                valueGetter: (params) => {
                    if (params.row.taggerIds == null)
                        return "";
                    return params.row.taggerIds;
                },
                renderCell: (params) => {
                    const tags = params.row.taggerIds;
                    if (!tags || tags.length === 0) {
                        return _jsx("span", {});
                    }
                    return _jsx(RenderTags, { namespace: taggerNamespace, tagIds: tags });
                },
            },
            {
                field: "namespace",
                headerName: t("sprava-textu.textsTable.namespace", "Jm. prostor"),
                sortable: true,
                width: 200,
            },
            {
                field: "cs",
                headerName: t("sprava-textu.textsTable.cs", "Česky"),
                sortable: true,
                flex: 1,
                valueGetter: (params) => {
                    const text = findDefaultTexts(params.row);
                    if (text == null)
                        return "";
                    return text.translations?.cs ?? "";
                },
            },
            {
                field: "en",
                headerName: t("sprava-textu.textsTable.en", "English"),
                sortable: true,
                flex: 1,
                valueGetter: (params) => {
                    const text = findDefaultTexts(params.row);
                    if (text == null)
                        return "";
                    return text.translations?.en ?? "";
                },
            },
            {
                field: "actions",
                type: "actions",
                width: 80,
                cellClassName: "btn-right",
                getActions: (params) => [
                    _jsx(GridActionsCellItem, { icon: _jsx(Edit, {}), label: t("sprava-textu.textsTable.editAction", "Upravit"), disabled: false, onClick: () => {
                            setEditText(params.row);
                            setNextText(rows[rows.indexOf(params.row) + 1] ?? null);
                            if (onEditText) {
                                onEditText(params.row);
                            }
                            navigate(`/keys/${params.row.namespace}|${params.row.key}`);
                        } }, "edit"),
                ],
            },
        ].filter(Boolean);
    }, [
        t,
        onEditText,
        setNextText,
        rows,
        navigate,
        tagFilterOperator,
        stateFilterOperator,
    ]);
    useEffect(() => {
        if (!onSelectedChange) {
            return;
        }
        if (selectedRowsData?.length === 0) {
            onSelectedChange(null);
        }
        else {
            onSelectedChange(selectedRowsData);
        }
    }, [selectedRowsData, onSelectedChange]);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    useEffect(() => {
        if (isSmallTable) {
            setColumnVisibilityModel({
                taggerIds: false,
                namespace: false,
                cs: false,
                en: false,
                actions: false,
            });
        }
        else {
            setColumnVisibilityModel({});
        }
    }, [isSmallTable]);
    const getRowIdHandler = (row) => {
        return row?.namespace + "|" + row?.key;
    };
    const isRowSelectableHandler = () => {
        return isRowSelectable ?? true;
    };
    const rowSelectionModelHandler = () => {
        return selectedRowsData
            ? selectedRowsData.map((row) => row.namespace + "|" + row.key)
            : [];
    };
    const onRowSelectionModelChangeHandler = (ids) => {
        if (rows == null || rows.length === 0) {
            return;
        }
        const selectedIDs = new Set(ids);
        const selectedRowData = rows.filter((row) => selectedIDs.has(row.namespace + "|" + row.key));
        setEditText(null);
        setNextText(null);
        return setSelectedRowsData(selectedRowData);
    };
    const getRowClassNameHandler = (params) => {
        if (params == null || params.row == null) {
            return null;
        }
        if (params.row.namespace + "|" + params.row.key === keyId)
            return "selected";
        if (editText == null) {
            return null;
        }
        return params.row.namespace + "|" + params.row.key ===
            editText.namespace + "|" + editText.key
            ? "selected"
            : null;
    };
    const onRowClickHandler = (row) => {
        setEditText(row.row);
        setNextText(rows[rows.indexOf(row.row) + 1] ?? null);
        navigate(`/keys/${row.row.namespace}|${row.row.key}`);
        return onEditText(row.row);
    };
    return (_jsx(DataGridPro, { columnVisibilityModel: columnVisibilityModel, columns: columns, rows: rowsValues, getRowId: getRowIdHandler, checkboxSelection: true, keepNonExistentRowsSelected: true, disableRowSelectionOnClick: true, isRowSelectable: canEdit ? isRowSelectableHandler : () => false, rowSelectionModel: rowSelectionModelHandler(), onRowSelectionModelChange: onRowSelectionModelChangeHandler, getRowClassName: getRowClassNameHandler, onFilterModelChange: onFilterChange, density: "compact", pageSizeOptions: [pageSize], pagination: true, onRowClick: onRowClickHandler, sx: colorClasses, slots: isSmallTable ? {} : { toolbar: GridToolbar }, slotProps: {
            toolbar: {
                showQuickFilter: true,
            },
        }, disableColumnSelector: true, localeText: csCZ.components.MuiDataGrid.defaultProps.localeText }));
};
